import { LoadingSpinner } from "@/components/common/LoadingSpinner/LoadingSpinner"
import { Input } from "@/components/ui/input"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { Search, X } from "lucide-react"
import { useState, type ReactNode } from "react"

interface MasterDataSearchProps {
  value: string
  onChange: (value: string) => void
  placeholder: string
  /** Show a spinner while the search API call is in flight */
  loading?: boolean
  /** Sort icon button, grouped with the filter toggle right after the search input. */
  sort?: ReactNode
  /** Filter toggle icon button, next to sort right after the search input. */
  filterToggle?: ReactNode
  /** Faceted filter chips — wraps onto its own row when the header runs out of width. */
  filterChips?: ReactNode
  /** Count/selection caption, rendered as the last row. */
  meta?: ReactNode
}

/** Search field + sort + filters + count caption — the single list header block for an entity list. */
export function MasterDataSearch({
  value,
  onChange,
  placeholder,
  loading,
  sort,
  filterToggle,
  filterChips,
  meta,
}: MasterDataSearchProps) {
  const { translate } = useTranslation()
  const [focused, setFocused] = useState(false)
  return (
    <div className="border-b border-grey4 px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <div
          className={cn(
            "relative min-w-[160px] flex-1 transition-[max-width] duration-200 ease-out",
            focused ? "max-w-[420px]" : "max-w-[240px]",
          )}
        >
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-grey5" />
          <Input
            value={value}
            onChange={(event) => onChange(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder={placeholder}
            className="border-grey4 bg-grey3 pl-9 pr-8 text-sm text-white placeholder:text-grey5"
          />
          {/* Right slot: spinner while loading, clear button when there's a value */}
          {loading ? (
            <div className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-grey5">
              <LoadingSpinner size={14} />
            </div>
          ) : value ? (
            <button
              type="button"
              onClick={() => onChange("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-grey5 hover:text-white"
              aria-label={translate("MC_MD_SEARCH_CLEAR_ARIA")}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {sort}
          {filterToggle}
        </div>
        {filterChips}
      </div>
      {meta && (
        <div className="mt-2 flex items-center text-xs text-grey5">{meta}</div>
      )}
    </div>
  )
}
